/**
 * Tarifs des fabricants à l'écran : ce que l'on dit à l'utilisateur, en français, avant et après un import.
 *
 * Fonctions PURES, sans React : c'est ici que se décident les phrases (« 480 hausses, 150 baisses… »), le
 * contrôle de la date d'effet avant l'envoi, et le nom d'une source (« Tarif KAESER pièces et service »).
 * Le serveur re-vérifie tout et reste l'autorité ; ces contrôles évitent seulement un aller-retour.
 */
import type { EcartsTarif, VersionTarif } from '../api/client';

const FORMAT_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** « 2026-01-01 » → « 01/01/2026 ». */
export function dateFrTarif(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

/** Un nombre à la française, avec ses milliers : 22609 → « 22 609 » (espace insécable fine de `Intl`). */
export function nombreFr(n: number): string {
  return new Intl.NumberFormat('fr-FR').format(n);
}

/**
 * Le nom d'une source de tarif : « Tarif KAESER pièces et service ». Un libellé qui commence déjà par
 * « tarif » (« Tarif 2026 ») n'est pas préfixé une seconde fois.
 */
export function nomDeSourceTarif(libelle: string): string {
  const propre = libelle.trim();
  return /^tarif\b/i.test(propre) ? propre : `Tarif ${propre}`;
}

/**
 * La date d'effet saisie : un message d'erreur, ou `null` si elle convient. Refuse une date absente, mal
 * formée, qui n'existe pas (31/02), ou antérieure à celle de la version en vigueur — le serveur la refuse
 * aussi, parce qu'une telle version ne s'appliquerait jamais.
 */
export function erreurDateDEffet(saisie: string, validFromEnVigueur: string | null): string | null {
  const m = FORMAT_DATE.exec(saisie);
  if (!m) return 'La date d’effet est obligatoire.';
  const annee = Number(m[1]);
  const mois = Number(m[2]);
  const jour = Number(m[3]);
  const date = new Date(Date.UTC(annee, mois - 1, jour));
  if (date.getUTCFullYear() !== annee || date.getUTCMonth() !== mois - 1 || date.getUTCDate() !== jour) {
    return `La date « ${dateFrTarif(saisie)} » n’existe pas.`;
  }
  if (validFromEnVigueur !== null && saisie < validFromEnVigueur) {
    return (
      `La date d’effet est antérieure à celle de la version en vigueur (${dateFrTarif(validFromEnVigueur)}) : ` +
      'cette version ne s’appliquerait jamais. Choisissez une date plus récente, ou archivez d’abord la version en vigueur.'
    );
  }
  return null;
}

/** Une variation en pourcentage : « +12,5 % », « −8 % », « — » quand l'ancien prix était nul. */
export function formaterVariation(variation: number | null): string {
  if (variation === null) return '—';
  const signe = variation > 0 ? '+' : variation < 0 ? '−' : '';
  return `${signe}${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(Math.abs(variation))} %`;
}

/** Un montant en euros : « 45,29 € ». */
export function eurosFr(montant: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant);
}

export interface PhraseEcart {
  readonly texte: string;
  /** « hausse » et « baisse » sont mis en couleur à l'écran ; les autres restent neutres. */
  readonly nature: 'hausse' | 'baisse' | 'neutre';
}

const accord = (n: number, singulier: string, pluriel: string): string => `${nombreFr(n)} ${n > 1 ? pluriel : singulier}`;

/** « 1 ligne », « 7 lignes », « 22 609 lignes » : le pluriel s'accorde, y compris dans les phrases d'aperçu. */
export const lignesFr = (n: number): string => accord(n, 'ligne', 'lignes');

/**
 * Les écarts en phrases, dans l'ordre utile : ce qui change d'abord (hausses, baisses, nouvelles,
 * disparues, textes modifiés), puis ce qui ne bouge pas. Une rubrique à zéro n'est pas montrée.
 */
export function decrireEcarts(ecarts: EcartsTarif): readonly PhraseEcart[] {
  const phrases: PhraseEcart[] = [];
  if (ecarts.hausses > 0) phrases.push({ texte: accord(ecarts.hausses, 'hausse de prix', 'hausses de prix'), nature: 'hausse' });
  if (ecarts.baisses > 0) phrases.push({ texte: accord(ecarts.baisses, 'baisse de prix', 'baisses de prix'), nature: 'baisse' });
  if (ecarts.nouvelles > 0) phrases.push({ texte: accord(ecarts.nouvelles, 'nouvelle référence', 'nouvelles références'), nature: 'neutre' });
  if (ecarts.disparues > 0) phrases.push({ texte: accord(ecarts.disparues, 'référence disparue', 'références disparues'), nature: 'neutre' });
  if (ecarts.descriptionsModifiees > 0) {
    phrases.push({ texte: accord(ecarts.descriptionsModifiees, 'désignation modifiée', 'désignations modifiées'), nature: 'neutre' });
  }
  if (ecarts.inchangees > 0) phrases.push({ texte: accord(ecarts.inchangees, 'référence inchangée', 'références inchangées'), nature: 'neutre' });
  return phrases;
}

/** « v2 · depuis le 01/01/2026 · 22 609 lignes » (+ « archivée » quand elle l'est). */
export function resumerVersionTarif(v: Pick<VersionTarif, 'versionNumber' | 'validFrom' | 'lineCount' | 'archivedAt'>): string {
  const base = `v${v.versionNumber} · depuis le ${dateFrTarif(v.validFrom)} · ${accord(v.lineCount, 'ligne', 'lignes')}`;
  return v.archivedAt ? `${base} · archivée` : base;
}

/** Le fichier choisi ressemble-t-il à un classeur Excel ? (Une aide : le serveur vérifie les octets.) */
export function ressembleAUnClasseur(nom: string): boolean {
  return /\.xlsx$/i.test(nom.trim());
}

/** Le message qui suit un import réussi. */
export function messageImportReussi(version: Pick<VersionTarif, 'versionNumber' | 'validFrom' | 'lineCount' | 'enVigueur'>): string {
  const effet = version.enVigueur
    ? `Elle est en vigueur depuis le ${dateFrTarif(version.validFrom)}.`
    : `Elle s’appliquera à partir du ${dateFrTarif(version.validFrom)} ; d’ici là, la version précédente reste en vigueur.`;
  return `Version ${version.versionNumber} importée : ${accord(version.lineCount, 'ligne', 'lignes')}. ${effet}`;
}
